import { useForm, defineRule, configure } from 'vee-validate';
import { required, email, max } from '@vee-validate/rules';
import { localize, setLocale } from '@vee-validate/i18n';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/composables/useApi';
import { useGoogleRecaptcha } from '@/composables/useGoogleRecaptcha';

defineRule('required', required);
defineRule('email', email);
defineRule('max', max);

export const useLeaveForm = (emit: (event: 'submit', ...args: any[]) => void) => {
  const { t, locale, messages } = useI18n();

  const { executeRecaptcha } = useGoogleRecaptcha();

  const { handleSubmit } = useForm();

  const fields: { [key: string]: any } = {
    name: {
      type: 'text',
      name: 'name',
      label: t('contact.form.name'),
      maxLength: 100,
      rules: 'required|max:100'
    },
    companyName: {
      type: 'text',
      name: 'companyName',
      label: t('contact.form.companyName'),
      maxLength: 100,
      rules: 'required|max:100'
    },
    email: {
      type: 'email',
      name: 'email',
      label: t('contact.form.email'),
      maxLength: 100,
      rules: 'required|email|max:100'
    },
    phone: {
      type: 'text',
      name: 'phone',
      label: t('contact.form.phone'),
      maxLength: 20,
      rules: 'required|max:20'
    }
  };

  const isSuccess = ref(false);
  const isError = ref(false);

  const onSubmit = handleSubmit(async (formData: any, { resetForm }) => {
    try {
      const { recaptchaData } = await executeRecaptcha('contact');

      await useApi('/contact-form/save', {
        method: 'POST',
        body: {
          name: formData.name,
          email: formData.email,
          companyName: formData.companyName,
          phone: formData.phone,
          consentPrivacyPolicy: formData.consentPrivacyPolicy,
          context: 'chat',
          vatNumber: null,
          zipCode: null,
          city: null,
          address: null,
          comment: null,
          consentMarketing: null,
          package: null,
          ...recaptchaData
        }
      });

      isSuccess.value = true;
      resetForm();
      emit('submit');
    } catch {
      isError.value = true;
    }
  });

  const updateFormLocale = () => {
    setLocale(locale.value);

    configure({
      generateMessage: localize({
        [locale.value]: {
          messages: (messages.value[locale.value] as any).validation ?? {},
          names: (messages.value[locale.value] as any).contact?.form ?? {},
          fields: {
            consentPrivacyPolicy: {
              required: t('contact.form.consentPrivacyPolicyRequired')
            }
          }
        }
      })
    });
  };

  watch(
    locale,
    () => {
      updateFormLocale();
    },
    { immediate: true }
  );

  return {
    fields,
    isSuccess,
    isError,
    onSubmit
  };
};
